/**
 * Text formatting helpers shared across the web app.
 */

/**
 * Convert a string to Title Case, preserving:
 *   - existing internal whitespace (single/multiple spaces, tabs, etc.)
 *   - tokens that already contain any uppercase letter (acronyms like
 *     PTSD/EMDR/NLP and proper names like McKenzie remain untouched)
 *   - hyphenated words (each segment is capitalized, e.g. "trauma-informed"
 *     -> "Trauma-Informed")
 *
 * Tokens that are entirely lowercase (or all non-letters) are normalized so
 * that the first letter of each hyphen-delimited segment is uppercased.
 *
 * Examples:
 *   toTitleCase("parts therapy")            -> "Parts Therapy"
 *   toTitleCase("breathwork")               -> "Breathwork"
 *   toTitleCase("childhood regression")     -> "Childhood Regression"
 *   toTitleCase("women's health")           -> "Women's Health"
 *   toTitleCase("trauma-informed care")     -> "Trauma-Informed Care"
 *   toTitleCase("PTSD recovery")            -> "PTSD Recovery"
 *   toTitleCase("EMDR")                     -> "EMDR"
 *
 * @param {string} str
 * @returns {string}
 */
export const toTitleCase = (str) => {
  if (typeof str !== 'string' || !str) return str || '';

  return str
    .split(/(\s+)/) // keep whitespace runs as their own tokens
    .map((token) => {
      // Preserve whitespace runs verbatim.
      if (/^\s+$/.test(token)) return token;
      // Preserve any token that already contains an uppercase letter
      // (acronyms like PTSD/EMDR, names like McKenzie, etc.).
      if (/[A-Z]/.test(token)) return token;

      // Capitalize each hyphen-delimited segment.
      return token
        .split('-')
        .map((seg) =>
          seg ? seg.charAt(0).toUpperCase() + seg.slice(1) : seg
        )
        .join('-');
    })
    .join('');
};
